import { supabase } from "./supabaseClient.js";
import { uploadFileToMinIO, deleteFileFromMinIO } from "./minioClient.js";

// ===== RECIPES =====
export async function listRecipes() {
  const { data, error } = await supabase
    .from("recipes")
    .select(`
      *,
      ingredient_images (
        image_url
      )
    `)
    .order("name");
  if (error) return [];
  
  // Retornar los datos incluyendo la primera imagen encontrada
  return (data || []).map(r => ({
    ...r,
    image_url: r.ingredient_images?.[0]?.image_url || null
  }));
}

export async function getRecipeById(id) {
  const { data, error } = await supabase
    .from("recipes")
    .select("*")
    .eq("id_recipes", id)
    .single();
  if (error) return null;
  return data;
}

// Convert time string (HH:MM) or number to integer minutes
function timeToMinutes(t) {
  try {
    if (!t) return null;
    if (typeof t === "number") return t;
    const [h, m] = String(t)
      .split(":")
      .map((x) => Number(x));
    if (Number.isFinite(h) && Number.isFinite(m)) return h * 60 + m;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

export async function createRecipe({
  name,
  description,
  portions,
  unit,
  preparation_time,
  notes,
  status_message,
  timer_started_at
}) {
  const { data, error } = await supabase
    .from("recipes")
    .insert({
      name,
      description: description ?? null,
      portions: Number(portions ?? 1),
      unit: unit ?? "porciones",
      preparation_time: timeToMinutes(preparation_time),
      notes: notes ?? null,
      status_message: status_message ?? null,
      timer_started_at: timer_started_at ?? null
    })
    .select("id_recipes")
    .single();
  if (error) throw error;
  return data?.id_recipes;
}

export async function updateRecipe(
  id,
  { name, description, portions, unit, preparation_time, notes, is_active, status_message, timer_started_at }
) {
  const fields = {};
  if (name !== undefined) fields.name = name;
  if (description !== undefined) fields.description = description ?? null;
  if (portions !== undefined) fields.portions = Number(portions);
  if (unit !== undefined) fields.unit = unit ?? "porciones";
  if (preparation_time !== undefined)
    fields.preparation_time = timeToMinutes(preparation_time);
  if (notes !== undefined) fields.notes = notes ?? null;
  if (is_active !== undefined) fields.is_active = Boolean(is_active);
  if (status_message !== undefined) fields.status_message = status_message ?? null;
  if (timer_started_at !== undefined) fields.timer_started_at = timer_started_at ?? null;

  const { error } = await supabase
    .from("recipes")
    .update(fields)
    .eq("id_recipes", id);
  return { ok: !error };
}

export async function adjustIngredientStock(ingredientName, targetQuantity, notes = "Ajuste manual") {
  const rawName = String(ingredientName || "").trim();
  if (!rawName) return { ok: false, error: "Nombre inválido" };
  const name = rawName.toLowerCase();

  // 1. Obtener stock actual (case insensitive para sumar todo lo existente)
  // Usamos ilike para buscar variantes (Tortilla, tortilla, TORTILLA)
  const { data: stocks, error: fetchError } = await supabase
    .from("stock_ingredients")
    .select("ingredient_name, kind, quantity")
    .ilike("ingredient_name", name);
    
  let currentStock = 0;
  if (stocks && stocks.length > 0) {
    stocks.forEach(row => {
      const q = Number(row.quantity || 0);
      const k = String(row.kind || "");
      const delta = k === "usage" ? -q : q;
      currentStock += delta;
    });
  }

  // 2. Calcular diferencia
  const diff = Number(targetQuantity) - Number(currentStock);
  if (Math.abs(diff) < 0.0001) return { ok: true }; // No hay cambio

  // 3. Insertar ajuste (siempre en minúsculas para normalizar)
  // Si diff es positivo (queremos subir stock), usamos 'adjustment' (suma)
  // Si diff es negativo (queremos bajar stock), usamos 'usage' (resta)
  const kind = diff > 0 ? "adjustment" : "usage";
  const qty = Math.abs(diff);

  const { error } = await supabase
    .from("stock_ingredients")
    .insert({
      ingredient_name: name,
      kind: kind,
      quantity: qty,
      notes: notes
    });

  return { ok: !error, error };
}

export async function deleteRecipe(id) {
  const { error } = await supabase
    .from("recipes")
    .delete()
    .eq("id_recipes", id);
  return { ok: !error };
}

// ===== RECIPE INGREDIENTS =====
export async function getRecipeIngredients(recipe_id) {
  const { data, error } = await supabase
    .from("v_recipes_with_ingredients")
    .select("*")
    .eq("id_recipes", recipe_id)
    .not("id_recipe_ingredients", "is", null);
  if (error) return [];
  return data || [];
}

export async function getRecipeIngredientById(id) {
  const { data, error } = await supabase
    .from("recipe_ingredients")
    .select("*")
    .eq("id_recipe_ingredients", id)
    .single();
  if (error) return null;
  return data;
}

export async function getRecipeIngredientsSafe(recipe_id) {
  const primary = await getRecipeIngredients(recipe_id);
  if (primary && primary.length) return primary;

  const { data: ri, error } = await supabase
    .from("recipe_ingredients")
    .select("id_recipe_ingredients, product_id, ingredient_name, quantity, unit")
    .eq("recipe_id", recipe_id);
  if (error || !ri || !ri.length) return [];

  const productIds = Array.from(
    new Set(ri.map((r) => r.product_id).filter(Boolean))
  );
  let namesById = {};
  if (productIds.length) {
    const { data: products } = await supabase
      .from("products")
      .select("id_products, name")
      .in("id_products", productIds);
    (products || []).forEach((p) => {
      namesById[p.id_products] = p.name;
    });
  }

  return ri.map((r) => ({
    id_recipe_ingredients: r.id_recipe_ingredients,
    product_id: r.product_id,
    product_name: r.ingredient_name || namesById[r.product_id] || "",
    quantity: r.quantity,
    unit: r.unit,
  }));
}

export async function addRecipeIngredient({
  recipe_id,
  ingredient_name,
  product_id,
  quantity,
  unit,
  notes,
}) {
  const qty = Number(quantity);
  if (!Number.isFinite(qty) || qty <= 0) {
    throw new Error("La cantidad del ingrediente debe ser mayor a 0");
  }

  const { data, error } = await supabase
    .from("recipe_ingredients")
    .insert({
      recipe_id,
      ingredient_name: ingredient_name ?? null,
      product_id: product_id ?? null,
      quantity: qty,
      unit: unit ?? null,
      notes: notes ?? null,
    })
    .select("id_recipe_ingredients")
    .single();
  if (error) throw error;
  return data?.id_recipe_ingredients;
}

export async function updateRecipeIngredient(id, { ingredient_name, quantity, unit, notes }) {
  const fields = {};
  if (ingredient_name !== undefined) fields.ingredient_name = ingredient_name ?? null;
  if (quantity !== undefined) fields.quantity = Number(quantity);
  if (unit !== undefined) fields.unit = unit ?? null;
  if (notes !== undefined) fields.notes = notes ?? null;

  const { error } = await supabase
    .from("recipe_ingredients")
    .update(fields)
    .eq("id_recipe_ingredients", id);
  return { ok: !error };
}

export async function deleteRecipeIngredient(id) {
  const { error } = await supabase
    .from("recipe_ingredients")
    .delete()
    .eq("id_recipe_ingredients", id);
  return { ok: !error };
}

// ===== INGREDIENT USAGE =====
export async function listIngredientUsage() {
  const { data, error } = await supabase
    .from("ingredient_usage")
    .select(`
      id_ingredient_usage,
      created_at,
      portions_made,
      notes,
      user_id,
      recipe_id,
      recipes (
        name,
        ingredient_images (
          image_url
        )
      ),
      users (
        username,
        full_name
      ),
      ingredient_usage_items (
        id_ingredient_usage_items,
        ingredient_name,
        quantity,
        unit,
        notes,
        product_id,
        products (
          name
        )
      )
    `)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching usage history:", error);
    return [];
  }

  // Transformar datos a estructura plana compatible con la vista anterior + imagen
  const flattened = [];

  for (const u of data) {
    const recipeName = u.recipes?.name || "-";
    // Tomar la primera imagen si existe (o buscar una lógica específica si se requiere)
    // ingredient_images es un array
    const recipeImage = u.recipes?.ingredient_images?.[0]?.image_url || null;
    
    const userName = u.users?.full_name || u.users?.username || "-";

    if (u.ingredient_usage_items && u.ingredient_usage_items.length > 0) {
      for (const item of u.ingredient_usage_items) {
        flattened.push({
          id_ingredient_usage: u.id_ingredient_usage,
          // Usar ID del item para key única en v-for
          unique_id: item.id_ingredient_usage_items, 
          created_at: u.created_at,
          recipe_name: recipeName,
          recipe_image: recipeImage,
          full_name: userName,
          portions_made: u.portions_made,
          usage_notes: u.notes,
          
          // Datos del item
          id_ingredient_usage_items: item.id_ingredient_usage_items,
          product_name: item.products?.name || item.ingredient_name || "-",
          quantity: item.quantity,
          unit: item.unit,
          item_notes: item.notes
        });
      }
    } else {
      // Uso sin items (caso raro)
      flattened.push({
        id_ingredient_usage: u.id_ingredient_usage,
        unique_id: `usage-${u.id_ingredient_usage}`,
        created_at: u.created_at,
        recipe_name: recipeName,
        recipe_image: recipeImage,
        full_name: userName,
        portions_made: u.portions_made,
        usage_notes: u.notes,
        
        id_ingredient_usage_items: null,
        product_name: "-",
        quantity: 0,
        unit: "",
        item_notes: null
      });
    }
  }

  return flattened;
}

export async function createIngredientUsage({
  recipe_id,
  user_id,
  portions_made,
  notes,
  items,
}) {
  // 1. Create usage record
  // NOTA IMPORTANTE: Insertamos inicialmente con recipe_id = null para evitar que el trigger
  // de la base de datos intente descontar automáticamente de la tabla 'recipe_ingredients'
  // y cause una violación de constraint (quantity > 0).
  // Actualizaremos el recipe_id al final.
  const { data: usage, error: usageError } = await supabase
    .from("ingredient_usage")
    .insert({
      recipe_id: null, 
      user_id: user_id ?? null,
      portions_made: Number(portions_made ?? 1),
      notes: notes ?? null,
    })
    .select("id_ingredient_usage")
    .single();

  if (usageError) throw usageError;
  const usageId = usage.id_ingredient_usage;

  // 2. Collect usage items (from params or recipe ingredients)
  let itemsToInsert = [];
  if (items && items.length > 0) {
    itemsToInsert = items.map((item) => ({
      usage_id: usageId,
      ingredient_name: ((String(item.product_name || "").trim().toLowerCase()) || null),
      quantity: Number(item.quantity ?? 0),
      unit: item.unit ?? null,
      notes: item.notes ?? null,
    }));
  } else if (recipe_id) {
    // Fallback: derive items from recipe_ingredients scaled by portions_made
    const { data: ri } = await supabase
      .from("recipe_ingredients")
      .select(
        "ingredient_name, quantity, unit"
      )
      .eq("recipe_id", recipe_id);
    const mult = Number(portions_made ?? 1);
    (ri || []).forEach((r) => {
      const qty = Number(r.quantity ?? 0) * mult;
      if (qty > 0) {
        itemsToInsert.push({
          usage_id: usageId,
          ingredient_name: ((String(r.ingredient_name || "").trim().toLowerCase()) || null),
          quantity: qty,
          unit: r.unit ?? null,
          notes: null,
        });
      }
    });
  }

  // 3. Insert usage items
  let insertedItems = [];
  if (itemsToInsert.length > 0) {
    const { data: itemsData, error: itemsError } = await supabase
      .from("ingredient_usage_items")
      .insert(itemsToInsert)
      .select(
        "id_ingredient_usage_items, usage_id, ingredient_name, quantity, unit, notes"
      );
    if (itemsError) throw itemsError;
    insertedItems = itemsData || [];
  }

  // Verificación y fallback: el trigger debe haber creado movimientos; si no, insertamos manualmente
  if (insertedItems.length > 0) {
    const { data: existingRows, error: chkErr } = await supabase
      .from("stock_ingredients")
      .select("usage_item_id")
      .eq("usage_id", usageId);
    if (chkErr) {
      throw new Error(`No se pudo verificar el descuento en stock_ingredients: ${chkErr.message}`);
    }
    const have = new Set((existingRows || []).map((r) => r.usage_item_id));
    const missing = insertedItems.filter((it) => !have.has(it.id_ingredient_usage_items));
    if (missing.length > 0) {
      const manual = missing.map((it) => ({
        ingredient_name: String(it.ingredient_name || "").trim().toLowerCase(),
        usage_item_id: it.id_ingredient_usage_items,
        usage_id: usageId,
        kind: "usage",
        quantity: Number(it.quantity || 0),
        unit: it.unit || null,
        notes: it.notes || null,
      }));
      const { error: insErr } = await supabase
        .from("stock_ingredients")
        .insert(manual);
      if (insErr) {
        throw new Error(`No se pudo crear el descuento en stock_ingredients: ${insErr.message}`);
      }
    }
  }

  // Bloque de inicialización posterior eliminado: ahora se hace antes del uso
  const usedNames = Array.from(
    new Set(
      (itemsToInsert || [])
        .map((it) => String(it.ingredient_name || "").trim().toLowerCase())
        .filter(Boolean)
    )
  );

  // 4. Actualizar el recipe_id en la tabla ingredient_usage si corresponde
  if (recipe_id) {
    const { error: updateErr } = await supabase
      .from("ingredient_usage")
      .update({ recipe_id: recipe_id })
      .eq("id_ingredient_usage", usageId);
    
    if (updateErr) {
      console.error("Error linking recipe to usage:", updateErr);
      // No lanzamos error aquí para no romper el flujo principal, ya que el uso y stock se registraron
    }
  }

  try {
    await emitStockAlertsForNames(usedNames);
  } catch {}
  return usageId;
}

export async function getIngredientStockByNames(names) {
  const arr = Array.from(
    new Set((names || []).map((n) => String(n || "").trim().toLowerCase()).filter(Boolean))
  );
  if (!arr.length) return [];
  
  // Fetch using the lowercased names. 
  // NOTE: If DB has "Tortilla" (Mixed case) and we query for "tortilla", .in() might miss it if collation is strict.
  // Ideally we would use .or() with ilike but that's complex for a list.
  // For now, we assume data is being normalized to lowercase by adjustIngredientStock/createIngredientUsage.
  // But to be safe for mixed legacy data, we might need to fetch all or use a different strategy.
  // However, since we can't easily do "IN (ILIKE...)" efficiently without RPC, we'll stick to .in() 
  // BUT we will normalize the aggregation in memory.
  
  const { data, error } = await supabase
    .from("stock_ingredients")
    .select("ingredient_name, kind, quantity")
    .in("ingredient_name", arr); 
    // This query is still weak against Mixed Case if not found. 
    // But fixing the query is hard without RPC. 
    // We rely on the fact that we are moving to lowercase.
    
  if (error) return [];
  const map = new Map();
  (data || []).forEach((row) => {
    const name = String(row.ingredient_name || "").toLowerCase(); // Normalize to lowercase
    const q = Number(row.quantity || 0);
    const k = String(row.kind || "");
    const cur = map.get(name) || 0;
    const delta = k === "usage" ? -q : q; // initial/adjustment sum, usage subtract
    map.set(name, cur + delta);
  });
  return Array.from(map.entries()).map(([ingredient_name, stock_qty]) => ({
    ingredient_name,
    stock_qty,
  }));
}

export async function getAllIngredientStocks() {
  const { data, error } = await supabase
    .from("stock_ingredients")
    .select("ingredient_name, kind, quantity");
  if (error) return [];
  const map = new Map();
  (data || []).forEach((row) => {
    const name = String(row.ingredient_name || "").toLowerCase(); // Normalize to lowercase
    const q = Number(row.quantity || 0);
    const k = String(row.kind || "");
    const cur = map.get(name) || 0;
    const delta = k === "usage" ? -q : q;
    map.set(name, cur + delta);
  });
  return Array.from(map.entries()).map(([ingredient_name, stock_qty]) => ({
    ingredient_name,
    stock_qty,
  }));
}

export async function getStockStatus(names) {
  const arr = Array.from(
    new Set((names || []).map((n) => String(n || "").trim().toLowerCase()).filter(Boolean))
  );
  if (!arr.length) return [];

  const { data, error } = await supabase
    .from("stock_ingredients")
    .select("ingredient_name, kind, quantity, created_at, usage_id")
    .in("ingredient_name", arr)
    .order("created_at", { ascending: true }); // Order by time asc for easier calc

  if (error) return [];

  // Group by ingredient
  const grouped = {};
  arr.forEach(n => grouped[n] = []);
  (data || []).forEach(r => {
    const n = String(r.ingredient_name || "").toLowerCase();
    if (grouped[n]) grouped[n].push(r);
  });

  return arr.map(name => {
    const rows = grouped[name] || [];
    let currentStock = 0;
    let lastRefillDate = null;
    let stockAtRefill = 0;

    // First pass: Calculate current stock and find last refill time
    // But to find StockAtRefill, we need the running balance at that moment.
    
    // Let's iterate chronologically
    rows.forEach(r => {
      const q = Number(r.quantity || 0);
      const k = String(r.kind || "");
      const delta = k === "usage" ? -q : q;
      
      currentStock += delta;
      
      // Check if this is a refill (positive input OR manual reset)
      // "initial" is always positive. 
      // "adjustment" is manual adjustment (usually positive).
      // "usage" without usage_id is a manual downward adjustment (Correction).
      // We treat ANY manual adjustment/initial as a "Refill" (Baseline Reset).
      
      const isRefill = (k === "initial") || 
                       (k === "adjustment") || 
                       (k === "usage" && !r.usage_id);
      
      if (isRefill) {
        lastRefillDate = r.created_at;
        stockAtRefill = currentStock; // Snapshot of stock right after this refill
      }
    });

    // If no refill ever found (only usages? or empty), fallback
    // If stockAtRefill is 0 (e.g. started 0, added 0?), avoid division by zero
    const basis = stockAtRefill > 0 ? stockAtRefill : (currentStock > 0 ? currentStock : 1);
    
    // Calculate percentage
    // Ratio = Current / StockAtRefill
    // If Current > StockAtRefill (e.g. data anomaly or float drift), cap at 100? No, let it be > 100.
    const ratio = currentStock / basis;
    
    return {
      ingredient_name: name,
      current_stock: currentStock,
      initial_basis: basis, // The "Reference" stock level
      ratio: ratio,
      percent: Math.round(ratio * 100)
    };
  });
}

export async function getInitialStockByNames(names) {
  const arr = Array.from(
    new Set((names || []).map((n) => String(n || "").trim().toLowerCase()).filter(Boolean))
  );
  if (!arr.length || !supabase) return [];
  const { data, error } = await supabase
    .from("stock_ingredients")
    .select("ingredient_name, kind, quantity")
    .in("ingredient_name", arr)
    .neq("kind", "usage");
  if (error) return [];
  const map = new Map();
  (data || []).forEach((r) => {
    const k = String(r.ingredient_name || "").toLowerCase();
    const v = Number(r.quantity || 0);
    map.set(k, (map.get(k) || 0) + v);
  });
  return Array.from(map.entries()).map(([ingredient_name, initial_qty]) => ({
    ingredient_name,
    initial_qty,
  }));
}

export async function emitStockAlertsForNames(names) {
  const arr = Array.from(
    new Set((names || []).map((n) => String(n || "").trim().toLowerCase()).filter(Boolean))
  );
  if (!supabase || !arr.length) return 0;

  const statuses = await getStockStatus(arr);
  
  const alerts = statuses.filter(s => {
    return s.initial_basis > 0 && s.ratio <= 0.5;
  });

  if (!alerts.length) return 0;

  const ch = supabase.channel("ops:stock_alerts");
  ch.subscribe();
  for (const a of alerts) {
    await ch.send({
      type: "broadcast",
      event: "stock:low",
      payload: {
        name: a.ingredient_name,
        current: a.current_stock,
        initial: a.initial_basis,
        percent: a.percent,
      },
    });
    try {
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("ops:stock:low", {
            detail: {
              name: a.ingredient_name,
              current: a.current_stock,
              initial: a.initial_basis,
              percent: a.percent,
            },
          })
        );
      }
    } catch {}
  }
  ch.unsubscribe();
  return alerts.length;
}

export function subscribeToStockAlerts(handler) {
  if (!supabase) return null;
  const ch = supabase.channel("ops:stock_alerts");
  ch.on("broadcast", { event: "stock:low" }, (payload) => {
    const p = payload && (payload.payload || payload);
    try {
      handler(p);
    } catch {}
  });
  ch.subscribe();
  return ch;
}

export async function listLowStockAlerts(threshold = 0.5) {
  if (!supabase) return [];
  
  const { data } = await supabase
    .from("stock_ingredients")
    .select("ingredient_name");
    
  const names = Array.from(new Set((data || []).map(r => String(r.ingredient_name || "").trim().toLowerCase()).filter(Boolean)));
  
  if (!names.length) return [];
  
  const statuses = await getStockStatus(names);
  
  return statuses
    .filter(s => s.initial_basis > 0 && s.ratio <= threshold)
    .map(s => ({
        name: s.ingredient_name,
        current: s.current_stock,
        initial: s.initial_basis,
        percent: s.percent
    }));
}

// ===== INGREDIENT IMAGES =====
export async function listIngredientImages() {
  const { data, error } = await supabase
    .from("ingredient_images")
    .select("id_ingredient_images, name, description, image_binary, image_url, mime, user_id, recipe_id, created_at, updated_at, recipes(name)")
    .order("created_at", { ascending: false });
  if (error) return [];
  const toBase64 = (hex) => {
    try {
      let s = String(hex || "");
      if (s.startsWith("\\x")) s = s.slice(2);
      if (!s) return "";
      const bytes = new Uint8Array(s.length / 2);
      for (let i = 0; i < s.length; i += 2) {
        bytes[i / 2] = parseInt(s.substr(i, 2), 16);
      }
      let bin = "";
      bytes.forEach((b) => (bin += String.fromCharCode(b)));
      return btoa(bin);
    } catch {
      return "";
    }
  };
  return (data || []).map((row) => ({
    ...row,
    image_base64: row.image_binary ? toBase64(row.image_binary) : null,
  }));
}

export async function createIngredientImage({
  name,
  description,
  image_base64,
  file,
  mime,
  user_id,
  recipe_id,
}) {
  let image_url = null;
  let image_binary = null;

  // 1. Intentar subir a MinIO si hay archivo
  if (file) {
    try {
      image_url = await uploadFileToMinIO(file, "ingredientes/");
    } catch (e) {
      console.error("Error subiendo a MinIO:", e);
      // Si falla MinIO, podríamos hacer fallback a binario o lanzar error.
      // El usuario pidió "no debe guardarse la imagen en la db", así que priorizamos MinIO.
      throw e; 
    }
  }

  // 2. Si no hay URL (no se subió a MinIO) y tenemos base64, 
  // podríamos guardarlo en DB como fallback SOLO si es absolutamente necesario,
  // pero la instrucción es usar buckets. 
  // Si no hay file ni url, y solo base64, intentamos convertir base64 a File y subir?
  // Por simplicidad, y siguiendo la instrucción, si tenemos URL usamos URL y binary null.
  
  if (!image_url && image_base64) {
    // Legacy fallback or if file not provided but base64 is.
    // Convertir base64 a bytea (hex con prefijo \\x)
    const base64ToHexPrefixed = (b64) => {
      try {
        const bin = atob(String(b64 || ""));
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        let hex = "";
        bytes.forEach((b) => (hex += b.toString(16).padStart(2, "0")));
        return "\\x" + hex;
      } catch {
        return null;
      }
    };
    image_binary = base64ToHexPrefixed(image_base64);
  }

  if (!image_url && !image_binary) throw new Error("Imagen inválida (se requiere archivo para MinIO o base64)");

  // Si tenemos image_url, preferimos NO guardar binary para ahorrar espacio en DB
  if (image_url) {
    image_binary = null;
  }

  const { data, error } = await supabase
    .from("ingredient_images")
    .insert({
      name,
      description: description ?? null,
      image_binary,
      image_url,
      mime: mime ?? "image/jpeg",
      user_id: user_id ?? null,
      recipe_id: recipe_id ?? null,
    })
    .select("id_ingredient_images")
    .single();
  if (error) throw error;
  return data?.id_ingredient_images;
}

export async function updateIngredientImage(id, { name, description, recipe_id, file }) {
  const fields = {};
  if (name !== undefined) fields.name = name;
  if (description !== undefined) fields.description = description ?? null;
  if (recipe_id !== undefined) fields.recipe_id = recipe_id || null;

  // Si hay nuevo archivo, subir a MinIO y reemplazar URL
  if (file) {
    try {
      // 1. Obtener imagen actual para borrar la vieja si existe
      const { data: currentImg } = await supabase
        .from("ingredient_images")
        .select("image_url")
        .eq("id_ingredient_images", id)
        .single();

      // 2. Subir nueva imagen
      const newUrl = await uploadFileToMinIO(file, "ingredientes/");
      fields.image_url = newUrl;
      fields.image_binary = null; // Limpiar binario si existía
      fields.mime = file.type;

      // 3. Borrar imagen anterior de MinIO si aplica
      if (currentImg && currentImg.image_url) {
        const minioEndpoint = import.meta.env.VITE_MINIO_ENDPOINT;
        const bucketName = import.meta.env.VITE_MINIO_BUCKET_FILES;
        const prefix = `${minioEndpoint.replace(/\/+$/, "")}/${bucketName}/`;
        
        if (currentImg.image_url.startsWith(prefix)) {
          const oldKey = currentImg.image_url.substring(prefix.length);
          if (oldKey) {
            // No await para no bloquear, o sí para asegurar limpieza? 
            // Mejor await para evitar condiciones de carrera si se borra muy rápido
            await deleteFileFromMinIO(oldKey).catch(err => console.error("Error borrando imagen vieja:", err));
          }
        }
      }
    } catch (e) {
      console.error("Error actualizando imagen en MinIO:", e);
      throw e;
    }
  }

  const { error } = await supabase
    .from("ingredient_images")
    .update(fields)
    .eq("id_ingredient_images", id);
  return { ok: !error };
}

export async function deleteIngredientImage(id) {
  // 1. Obtener la URL de la imagen antes de eliminar el registro
  const { data: img, error: fetchError } = await supabase
    .from("ingredient_images")
    .select("image_url")
    .eq("id_ingredient_images", id)
    .single();

  if (!fetchError && img && img.image_url) {
    try {
      // Intentar extraer la key de MinIO
      // Formato esperado: http://localhost:9000/tranquero-files/ingredientes/filename
      const minioEndpoint = import.meta.env.VITE_MINIO_ENDPOINT;
      const bucketName = import.meta.env.VITE_MINIO_BUCKET_FILES;
      
      // Construir el prefijo a remover: endpoint + / + bucket + /
      // Manejar posibles slashes finales en endpoint
      const prefix = `${minioEndpoint.replace(/\/+$/, "")}/${bucketName}/`;
      
      if (img.image_url.startsWith(prefix)) {
        const key = img.image_url.substring(prefix.length);
        if (key) {
          await deleteFileFromMinIO(key);
        }
      }
    } catch (err) {
      console.error("Error intentando eliminar archivo de MinIO:", err);
      // No bloqueamos la eliminación de la DB si falla MinIO, pero logueamos
    }
  }

  const { error } = await supabase
    .from("ingredient_images")
    .delete()
    .eq("id_ingredient_images", id);
  return { ok: !error };
}
